// Imports for external libraries go here.
import React, { FC, useEffect } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { RoundUpItem, RoundUpModel, RoundUpProps } from './RoundUp.types';
import { StyledRoundUp } from './RoundUp.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Image, Link } from '@marriott/mi-ui-library';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import { useState } from 'react';
import { debounce, useDCADynamicModelAlloy } from '../../modules/helpers';
import { BRIGHTCOVE_SRC, VARIATION_ONE, VARIATION_TWO } from '../../modules/constants/constant';

interface PlayerInstance {
  paused: () => boolean;
  pause: () => void;
  play: () => void;
}

const variationOne = (
  roundUpItems: RoundUpItem[],
  totalItems: number,
  enableTracking: boolean = false,
  showNumbers?: boolean
) => {
  return roundUpItems?.map((roundUpItem: RoundUpItem, index: number) => {
    const sanitizedNode = DOMPurify.sanitize(roundUpItem?.description as string, { RETURN_DOM: true }) as HTMLElement;
    const fragmentString = sanitizedNode.innerHTML;

    return (
      <div
        key={index}
        id={roundUpItem?.anchorId}
        className={clsx(
          'row mx-0 d-flex align-items-center',
          index === 0 ? 'roundup-first-item' : index === totalItems - 1 ? '' : 'roundup-spacing'
        )}
      >
        <div
          className={clsx(
            'offset-xl-1 col-12 col-md-7 col-xl-4 order-1 px-0 px-md-2',
            index % 2 === 1 ? 'order-md-1 offset-md-0' : 'order-md-0'
          )}
        >
          {showNumbers && (
            <div className="roundup-number">
              <span className="t-title-l separator px-3 roundup-title">{index + 1}</span>
            </div>
          )}
          <div className="t-title-m pb-3 pt-3">{roundUpItem.title}</div>
          <div className="t-font-alt-m pb-3" dangerouslySetInnerHTML={{ __html: fragmentString ?? '' }}></div>
          <div className="roundup-cta">
            <Link
              text={roundUpItem?.ctaLabel}
              linkClassName={clsx('m-link roundup-cta-link', enableTracking ? 'custom_click_track' : '')}
              linkHref={roundUpItem?.ctaLink || ''}
              target={roundUpItem.openInANewTab ? '_blank' : '_self'}
              linkType="internal"
              custom_click_track_value={enableTracking ? roundUpItem.clickTrackValue || '' : ''}
            />
          </div>
        </div>
        <div className="offset-xl-1 col-12 col-md-5 px-0 px-md-2">
          <Image
            customClass={'roundup-image'}
            dynamic={true}
            defaultImageURL={roundUpItem?.dynamicImage?.assetPath}
            renditions={roundUpItem?.dynamicImage?.renditions}
            altText={roundUpItem?.dynamicImage?.altText}
          />
        </div>
      </div>
    );
  });
};

const variationTwoWithoutHighlightCard = (
  roundUpItems: RoundUpItem[],
  totalItems: number,
  showHighlightCard: boolean,
  ofLabel?: string,
  enableTracking: boolean = false,
  showNumbers?: boolean
) => {
  return roundUpItems?.map((roundUpItem: RoundUpItem, index: number) => {
    const sanitizedNode = DOMPurify.sanitize(roundUpItem?.description as string, { RETURN_DOM: true }) as HTMLElement;
    const fragmentString = sanitizedNode.innerHTML;

    return (
      <div
        id={roundUpItem?.anchorId}
        key={index}
        className={clsx('col-xl-5 col-md-6 col-12 roundup-other-slide px-0 px-md-2')}
      >
        <div>
          <Image
            customClass={'roundup-image'}
            dynamic={true}
            defaultImageURL={roundUpItem?.dynamicImage?.assetPath}
            renditions={roundUpItem?.dynamicImage?.renditions}
            altText={roundUpItem?.dynamicImage?.altText}
          />
        </div>
        {showNumbers && (
          <div className="t-title-l roundup-number number-up">
            <span className="d-flex justify-content-center align-items-center px-3 py-2">
              {index + (showHighlightCard ? 2 : 1)}
              <span className="t-font-xs pl-1 sub-number">
                {' '}
                {ofLabel} {totalItems + (showHighlightCard ? 1 : 0)}
              </span>
            </span>
          </div>
        )}
        <div className="card-details-wrapper">
          <div className="t-title-m pt-4 pt-md-3 pb-3">{roundUpItem.title}</div>
          <div className="t-font-alt-m pb-3" dangerouslySetInnerHTML={{ __html: fragmentString ?? '' }}></div>
          <div className="roundup-cta">
            <Link
              text={roundUpItem?.ctaLabel}
              linkClassName={clsx('m-link roundup-cta-link', enableTracking ? 'custom_click_track' : '')}
              linkHref={roundUpItem?.ctaLink || ''}
              target={roundUpItem.openInANewTab ? '_blank' : '_self'}
              linkType="internal"
              custom_click_track_value={enableTracking ? roundUpItem.clickTrackValue || '' : ''}
            />
          </div>
        </div>
      </div>
    );
  });
};

const highlightImageorVideo = (componentValues: RoundUpModel) => {
  const assetType = componentValues?.assetProperties?.assetType?.toLowerCase();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [player, setPlayer] = useState<PlayerInstance | null>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [viewPort, setViewPort] = useState('');

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);
    handleResize();
    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 767px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else {
      setViewPort('viewPortM');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attachAnalyticsAttr = (success: any) => {
    const { ref } = success;
    if (!ref) return;
    ref.controls_ = false;
    setPlayer(ref);
  };

  const handleClickEvent = () => {
    if (player) {
      if (player.paused()) {
        player.play();
        setVideoPlaying(true);
      } else {
        player.pause();
        setVideoPlaying(false);
      }
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlayPause = (event: any) => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      if (player) {
        if (player.paused()) {
          player.play();
          setVideoPlaying(true);
        } else {
          player.pause();
          setVideoPlaying(false);
        }
      }
    }
  };
  const options = {
    autoplay: true,
    muted: true,
    aspectRatio: '16:9',
    loop: true,
    src: BRIGHTCOVE_SRC,
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [videoPlaying, setVideoPlaying] = useState<boolean>(options?.autoplay);

  const sanitizedNode = DOMPurify.sanitize(componentValues?.cardDescription as string, {
    RETURN_DOM: true,
  }) as HTMLElement;
  const fragmentString = sanitizedNode.innerHTML;

  return (
    <div id={componentValues?.cardAnchorId} className={clsx('highlight-card', `highlight-card-${assetType}`)}>
      {assetType === 'image' && (
        <div className={clsx('roundup-highlight-img px-0 col-xl-12')}>
          <Image
            customClass={'roundup-highlight-image'}
            dynamic={componentValues?.dynamicCardImage?.dynamic}
            defaultImageURL={componentValues?.dynamicCardImage?.assetPath}
            renditions={componentValues?.dynamicCardImage?.renditions}
            altText={componentValues?.dynamicCardImage?.altText}
          />
        </div>
      )}
      {assetType === 'cinemograph' &&
        componentValues?.assetProperties?.videoAccountId &&
        componentValues?.assetProperties?.brightcoveVideoId && (
          <div className={clsx('roundup-highlight-cinemograph px-0 col-xl-12')}>
            <ReactPlayerLoader
              id={`luxury-roundup-${componentValues?.assetProperties?.videoAccountId}`}
              accountId={componentValues?.assetProperties?.videoAccountId as string}
              options={options}
              videoId={
                viewPort !== 'Mobile'
                  ? (componentValues?.assetProperties?.brightcoveVideoId as string)
                  : ((componentValues?.assetProperties?.brightcoveMobileVideoId
                      ? componentValues?.assetProperties?.brightcoveMobileVideoId
                      : componentValues?.assetProperties?.brightcoveVideoId) as string)
              }
              playerId={componentValues?.assetProperties?.playerId ?? 'default'}
              poster={componentValues?.assetProperties?.videoThumbnail ?? ''}
              onSuccess={attachAnalyticsAttr}
            />
            <div
              className={clsx('icon-play-pause custom_click_track ')}
              onKeyDown={e => handlePlayPause(e)}
              onClick={() => {
                handleClickEvent();
              }}
              tabIndex={0}
              custom_click_track_value={`roundup ${componentValues?.assetProperties?.assetType} | ${
                videoPlaying ? componentValues?.pauseLabel : componentValues?.playLabel
              } button | internal`}
              role="button"
            >
              <span>
                <span className={clsx('icon-l', !videoPlaying ? 'icon-play' : 'icon-pause')}></span>
                <span className="sr-only">
                  {!videoPlaying ? componentValues?.playLabel : componentValues?.pauseLabel}
                </span>
              </span>
            </div>
          </div>
        )}
      <div className="highlight-card-details-wrapper row px-4 px-xl-0 justify-content-md-center">
        {componentValues?.showNumbers && (
          <div className="t-title-l roundup-highlight-number highlight-number-up col-4 col-md-2 col-xl-1 pb-3">
            <span className="d-flex justify-content-center align-items-center separator px-3">
              {1}
              <span className="t-font-xs pl-1 sub-number">
                {' '}
                {componentValues?.ofLabel} {componentValues?.totalItems + 1}
              </span>
            </span>
          </div>
        )}
        <div className="t-title-m col-12 col-md-5 col-xl-3">{componentValues?.cardTitle}</div>
        <div className="col-12 col-md-5 col-xl-6">
          <div className="t-font-alt-m pb-4" dangerouslySetInnerHTML={{ __html: fragmentString ?? '' }}></div>
          <div className="roundup-cta">
            <Link
              text={componentValues?.cardCtaLabel}
              linkClassName={clsx(
                'm-link roundup-cta-link',
                componentValues?.enableTracking ? 'custom_click_track' : ''
              )}
              linkHref={componentValues?.cardCtaLink || ''}
              target={componentValues?.cardOpenInANewTab ? '_blank' : '_self'}
              linkType="internal"
              custom_click_track_value={componentValues?.enableTracking ? componentValues.clickTrackValue || '' : ''}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Use named rather than default exports.
export const RoundUp: FC<RoundUpProps> = (props: RoundUpProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: RoundUpModel = targetData ? targetData : props?.model;

  const variation1 = componentValues?.variation?.toLowerCase() === VARIATION_ONE;
  const variation2 = componentValues?.variation?.toLowerCase() === VARIATION_TWO;
  return (
    <StyledRoundUp className={componentValues?.styleclass}>
      <div className="roundup roundup-wrapper">
        <div className={clsx('container', variation1 ? 'px-3 px-md-0' : '', variation2 ? 'px-0' : '')}>
          {variation1 &&
            variationOne(
              componentValues?.roundUpItems,
              componentValues?.totalItems,
              componentValues.enableTracking,
              componentValues?.showNumbers
            )}
          {variation2 && (
            <div className="highlight-wrapper">
              {componentValues.showHighlightCard && (
                <div className="highlight-card-section">{highlightImageorVideo(componentValues)}</div>
              )}
              <div className="cards-column-section__highlight row px-3 px-md-0 mx-0">
                {variationTwoWithoutHighlightCard(
                  componentValues?.roundUpItems,
                  componentValues.totalItems,
                  componentValues.showHighlightCard,
                  componentValues?.ofLabel,
                  componentValues.enableTracking,
                  componentValues?.showNumbers
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </StyledRoundUp>
  );
};

export const RoundUpConfig = {
  emptyLabel: 'LuxuryRoundUp',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/round-up/v1/round-up`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RoundUpEditable = (props: any) => (
  <div data-testid="luxury-RoundUp" data-component-name="m-luxury-RoundUp">
    <EditableComponent config={RoundUpConfig} {...props}>
      <RoundUp {...props} />
    </EditableComponent>
  </div>
);
